// Paste this entire file replacing your current LamfPage.jsx
import React, { useState, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Slider,
    Avatar,
    Tooltip,
    Divider,
    useMediaQuery,
    ToggleButtonGroup,
    ToggleButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    CardActionArea
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ArrowForward as ArrowForwardIcon,
    CheckCircleOutline as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    Security as SecurityIcon,
    Savings as SavingsIcon,
    CreditCard as CreditCardIcon,
    AccountBalance,
    Link as LinkIcon,
    MonetizationOn as MoneyIcon,
    Close as CloseIcon,
    Launch as LaunchIcon,
    TouchApp as TouchAppIcon
} from '@mui/icons-material';
import { ThemeProvider, keyframes } from '@mui/material/styles';
import SEO from '../components/SEO/SEO';
import { getSeoData } from '../utils/seoConfig';
import { getAppTheme } from '../services/authContext';
import { lamfApi } from '../services/mle-api';

const useSelector = (selector) => ({ mode: 'light' });

// ---------- CONFIG & DATA ----------
const PROVIDER_2_LINK = "https://www.makeloaneazy.50fin.in/signin/otp/";

const heroData = {
    title: "Digital Loan Against Mutual Funds (LAMF)",
    subtitle: "Money credited within 2 working hours ✨",
    features: [
        "Keep market exposure — no need to redeem units",
        "Quick approval & minimal documentation"
    ],
    heroIllustration: "/img_frontend/mle/undraw_savings_uwjn.svg"
};

const benefitsData = [
    { title: "Keep Market Exposure", description: "You retain your mutual fund holdings and still benefit from market upside while accessing liquidity.", icon: <TrendingUpIcon /> },
    { title: "High Loan-to-Value (LTV)", description: "Borrow up to a high percentage of your portfolio value depending on fund type—often 50%-80%.", icon: <CreditCardIcon /> },
    { title: "No Need to Redeem Units", description: "Avoid triggering capital gains tax and stay invested while using funds for other needs.", icon: <SecurityIcon /> },
    { title: "Quick Disbursal", description: "Minimal documentation and fast processing—get funds disbursed quickly for emergencies or opportunities.", icon: <SavingsIcon /> },
];

const alternatingBenefitsData = [
    {
        title: "Flexible Tenure & Repayment",
        description: "Choose repayment schedules that suit you — bullet repayment, EMIs or part prepayment options. Tenure depending on lender policy.",
        imageUrl: "/img_frontend/mle/mleh3.jpg"
    },
    {
        title: "Maintain Tax Efficiency",
        description: "Since you don't redeem mutual funds, there's no capital gains event at disbursal — helping you keep tax efficiency intact.",
        imageUrl: "/img_frontend/mle/mleh4.jpg"
    }
];

const faqData = [
    { q: 'What is Loan Against Mutual Funds (LAMF)?', a: 'LAMF lets you pledge your mutual fund units as collateral to borrow funds without selling them. Loan amount is determined by the eligible portfolio value and the lender’s approved LTV.' },
    { q: 'How is the eligible loan amount calculated?', a: 'Lenders use an LTV (Loan-to-Value) percentage on the eligible value of your mutual fund holdings. For example, if your portfolio is ₹1,00,000 and LTV is 75%, eligible loan is ₹75,000.' },
    { q: 'Will I be taxed when I take this loan?', a: 'No — taking a loan against your mutual funds is not a sale, so it does not trigger capital gains taxes. Taxes apply only if you redeem units.' },
    { q: 'What happens if my fund value falls?', a: 'If the portfolio value falls and the LTV threshold is breached, the lender may ask for additional collateral or partial repayment. Check lender terms for margin-call rules.' },
];

// ---------- ANIMATIONS & STYLES ----------
const floatUp = keyframes`
  0% { transform: translateY(0px); opacity: 0.98; }
  50% { transform: translateY(-6px); opacity: 1; }
  100% { transform: translateY(0px); opacity: 0.98; }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(31,211,167, 0.28); transform: scale(1); }
  70% { box-shadow: 0 0 0 18px rgba(31,211,167, 0); transform: scale(1.02); }
  100% { box-shadow: 0 0 0 0 rgba(31,211,167, 0); transform: scale(1); }
`;

const shine = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const arrowRotate = keyframes`
  0% { transform: translateX(0) rotate(0deg); }
  50% { transform: translateX(4px) rotate(10deg); }
  100% { transform: translateX(0) rotate(0deg); }
`;

// ---------- SDK INIT ----------
const lasSdkInstance = typeof window !== "undefined" && window.ScLoan
    ? new window.ScLoan({ gatewayName: "makeloaneazy" })
    : null;


// ---------- REUSABLE COMPONENTS ----------
const Section = ({ children, sx = {}, ...props }) => (
    <Box component="section" sx={{ py: { xs: 5, md: 8 }, ...sx }} {...props}>
        <Container maxWidth="lg">{children}</Container>
    </Box>
);

const SectionTitle = ({ title, subtitle, align = 'center' }) => (
    <Box sx={{ textAlign: align, mb: 4 }}>
        <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 700 }}>{title}</Typography>
        {subtitle && <Typography variant="body1" color="text.secondary">{subtitle}</Typography>}
    </Box>
);

const ValueLabelComponent = (props) => {
    const { children, value } = props;
    return <Tooltip enterTouchDelay={0} placement="top" title={value.toLocaleString('en-IN')}>{children}</Tooltip>;
};

const CalculatorInput = ({ label, value, onChange, unit, min, max, step }) => (
    <Box mb={2}>
        <Typography gutterBottom sx={{ fontWeight: 600 }}>{label}</Typography>
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
                <Slider
                    value={value}
                    onChange={(e, val) => onChange(val)}
                    min={min}
                    max={max}
                    step={step}
                    components={{ ValueLabel: ValueLabelComponent }}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField
                    value={typeof value === 'number' ? value.toLocaleString('en-IN') : value}
                    onChange={(e) => {
                        const numericValue = Number(e.target.value.replace(/,/g, ''));
                        if (!isNaN(numericValue)) onChange(numericValue > max ? max : numericValue);
                    }}
                    size="small"
                    InputProps={{ startAdornment: <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>{unit}</Typography> }}
                    variant="outlined"
                    fullWidth
                />
            </Grid>
        </Grid>
    </Box>
);


// ---------- SECTIONS ----------
const HeroSection = ({ data, onApplyClick }) => {
    const theme = getAppTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    return (
        <Section sx={{
            background: 'linear-gradient(135deg, #175d55 0%, #104652 100%)',
            color: 'white',
            boxShadow: '0 20px 50px rgba(10, 70, 80, 0.15)',
            position: 'relative',
            overflow: 'visible',
            pt: { xs: 6, md: 10 },
            pb: { xs: 6, md: 10 }
        }}>
            <Container maxWidth="lg">
                <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant={isMdUp ? "h2" : "h4"} component="h1" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                            {data.title}
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 3, color: 'rgba(255,255,255,0.85)' }}>{data.subtitle}</Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {data.features.map((feature, i) => (
                                <Box key={i} sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.25,
                                    bgcolor: 'rgba(255,255,255,0.04)', p: 1, borderRadius: 2, width: 'fit-content'
                                }}>
                                    <CheckCircleIcon sx={{ color: 'rgba(255,255,255,0.9)' }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>{feature}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ mt: 4, display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                                onClick={onApplyClick}
                                endIcon={<ArrowForwardIcon sx={{ animation: `${arrowRotate} 1400ms ease-in-out infinite` }} />}
                                sx={{
                                    background: 'linear-gradient(90deg,#1fd3a7,#05b2a0)',
                                    color: 'white',
                                    px: 3.5, py: 1.2,
                                    borderRadius: 30,
                                    boxShadow: '0 10px 30px rgba(5,178,160,0.22)',
                                    textTransform: 'none',
                                    fontWeight: 800,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    '&:before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'linear-gradient(120deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.08) 100%)',
                                        opacity: 0,
                                        transition: 'opacity 320ms ease'
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-4px) scale(1.01)',
                                        boxShadow: '0 20px 50px rgba(5,178,160,0.28)',
                                        '&:before': { opacity: 1, animation: `${shine} 1.6s linear infinite` }
                                    },
                                    '&:active': { transform: 'translateY(-1px) scale(0.995)' },
                                    animation: `${pulse} 3.6s infinite`
                                }}
                            >
                                <Box sx={{ position: 'relative', zIndex: 2 }}>Apply for Loan Against Mutual Fund</Box>
                            </Button>
                        </Box>

                        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.7)' }}>
                            Money credited within 2 working hours
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{
                            transform: 'translateY(-12px)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <Paper elevation={6} sx={{
                                width: '100%', maxWidth: 520, borderRadius: 3, p: 3,
                                background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,255,255,0.94))',
                                boxShadow: '0 30px 80px rgba(7,45,52,0.08)',
                                animation: `${floatUp} 6s ease-in-out infinite`
                            }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={7}>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#08373a' }}>Direct to your bank Account</Typography>
                                    </Grid>
                                    <Grid item xs={5} sx={{ textAlign: 'center' }}>
                                        <img src={data.heroIllustration} alt="credit" style={{ width: '100%', maxWidth: 140 }} />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <Button variant="contained" onClick={onApplyClick} size="small" sx={{
                                            background: 'linear-gradient(90deg,#1fd3a7,#05b2a0)',
                                            borderRadius: 20,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            px: 2.5, py: 0.8,
                                            transition: 'transform 220ms ease, box-shadow 220ms ease',
                                            '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 14px 40px rgba(5,178,160,0.18)' }
                                        }}>Apply now</Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Section>
    );
};

const RateBand = () => (
    <Box sx={{ background: 'linear-gradient(90deg, rgba(20,184,166,0.06), rgba(163,230,203,0.03))', py: 1.5 }}>
        <Container maxWidth="lg">
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} sm={3} sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                    <Typography variant="caption" color="text.secondary">Auto Loan</Typography>
                    <Typography variant="h6">12-15%</Typography>
                </Grid>
                <Grid item xs={6} sm={3} sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                    <Typography variant="caption" color="text.secondary">Consumer Loan</Typography>
                    <Typography variant="h6">14-28%</Typography>
                </Grid>
                <Grid item xs={6} sm={3} sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                    <Typography variant="caption" color="text.secondary">Credit Card</Typography>
                    <Typography variant="h6">24-36%</Typography>
                </Grid>
                <Grid item xs={6} sm={3} sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                    <Typography variant="caption" color="text.secondary">Loan Against Mutual Funds</Typography>
                    <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 800 }}>10.4%</Typography>
                </Grid>
            </Grid>
        </Container>
    </Box>
);

const CustomSectionTitle = ({ title, subtitle, align = 'center', sx = {}, ...props }) => (
    <Box sx={{ textAlign: align, mb: 4, ...sx }} {...props}>
        <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 700 }}>
            {title}
        </Typography>
        {subtitle && <Typography variant="body1" color="text.secondary">{subtitle}</Typography>}
    </Box>
);

const StepsSection = () => (
    <Section>
        <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
                <CustomSectionTitle sx={{ color: "#185b6e" }} title="Cash against your mutual funds in 4 easy steps" subtitle="Fast, paperless — funds to your account" align="left" />
                <List>
                    <ListItem sx={{ py: 1 }}>
                        <ListItemIcon><MoneyIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Calculate credit limit" secondary="Estimate how much you can borrow" />
                    </ListItem>
                    <ListItem sx={{ py: 1 }}>
                        <ListItemIcon><AccountBalance color="primary" /></ListItemIcon>
                        <ListItemText primary="Confirm details" secondary="Provide basic KYC & bank details" />
                    </ListItem>
                    <ListItem sx={{ py: 1 }}>
                        <ListItemIcon><LinkIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Link bank account" secondary="To receive funds directly" />
                    </ListItem>
                    <ListItem sx={{ py: 1 }}>
                        <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Pledge mutual funds" secondary="We will place a pledge on your folio" />
                    </ListItem>
                </List>
            </Grid>

            <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{
                    p: 3, borderRadius: 3, display: 'flex', justifyContent: 'center', alignItems: 'center',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,251,250,0.95))'
                }}>
                    <img src="/img_frontend/mlel6.jpg" alt="steps" style={{ maxWidth: '100%', height: 'auto' }} />
                </Paper>
            </Grid>
        </Grid>
    </Section>
);

const LAMFCalculatorSection = ({ onApplyClick }) => {
    const [mode, setMode] = useState('loan');
    const [portfolioValue, setPortfolioValue] = useState(200000);
    const [ltv, setLtv] = useState(50);
    const [rate, setRate] = useState(12);
    const [tenure, setTenure] = useState(12);
    const [desiredLoan, setDesiredLoan] = useState(100000);

    const handleModeChange = (event, newMode) => { if (newMode !== null) setMode(newMode); };

    const { eligibleLoan } = useMemo(() => {
        const eligible = Math.floor((portfolioValue * ltv) / 100);
        return { eligibleLoan: eligible };
    }, [portfolioValue, ltv]);

    const { emiForLoan, totalInterest } = useMemo(() => {
        const principal = mode === 'loan' ? eligibleLoan : desiredLoan;
        const monthlyInterestPayment = Math.floor((principal * (rate / 100)) / 12);
        const n = Math.max(1, Math.round(tenure));
        const totalInt = monthlyInterestPayment * n;

        return {
            emiForLoan: monthlyInterestPayment,
            totalPayable: principal + totalInt,
            totalInterest: totalInt
        };
    }, [eligibleLoan, desiredLoan, rate, tenure, mode]);

    return (
        <Section sx={{ background: 'linear-gradient(180deg, rgba(247,250,249,1), rgba(252,253,252,1))' }}>
            <SectionTitle title="Loan Against Mutual Funds Calculator" subtitle="Estimate eligible loan or monthly interest based on your portfolio" align="left" />
            <Grid container justifyContent="center">
                <Grid item xs={12} md={10} lg={8}>
                    <Paper elevation={6} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
                        <ToggleButtonGroup
                            color="primary"
                            value={mode}
                            exclusive
                            onChange={handleModeChange}
                            aria-label="calculator mode"
                            fullWidth
                            sx={{ mb: 4, borderRadius: 2 }}
                        >
                            <ToggleButton value="loan" sx={{ fontWeight: 700 }}>Calculate Eligible Loan</ToggleButton>
                            <ToggleButton value="emi" sx={{ fontWeight: 700 }}>Calculate Interest for Desired Loan</ToggleButton>
                        </ToggleButtonGroup>

                        {mode === 'loan' ? (
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={7}>
                                    <CalculatorInput label="Portfolio Value" value={portfolioValue} onChange={setPortfolioValue} unit="₹" min={5000} max={100000000} step={5000} />
                                    <CalculatorInput label="LTV (Loan-to-Value %)" value={ltv} onChange={setLtv} unit="%" min={45} max={90} step={1} />
                                    <CalculatorInput label="Interest Rate (expected)" value={rate} onChange={setRate} unit="% p.a." min={4} max={24} step={0.1} />
                                </Grid>

                                <Grid item xs={12} md={5} sx={{ textAlign: 'center', alignSelf: 'center' }}>
                                    <Typography color="text.secondary">Eligible Loan Amount</Typography>
                                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 800 }}>₹{eligibleLoan.toLocaleString('en-IN')}</Typography>

                                    <Typography color="text.secondary" sx={{ mt: 2 }}>Monthly Interest Payment</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>₹{emiForLoan.toLocaleString('en-IN', { maximumFractionDigits: 0 })} / month</Typography>

                                    <Button variant="contained" onClick={onApplyClick} sx={{
                                        mt: 2, px: 4, py: 1.2, borderRadius: 28, background: 'linear-gradient(90deg,#1fd3a7,#05b2a0)', fontWeight: 800,
                                        transition: 'transform 180ms ease, box-shadow 180ms ease',
                                        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 14px 40px rgba(3,80,72,0.14)' }
                                    }}>Apply for Loan</Button>
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={7}>
                                    <CalculatorInput label="Desired Loan Amount" value={desiredLoan} onChange={setDesiredLoan} unit="₹" min={5000} max={100000000} step={5000} />
                                    <CalculatorInput label="Tenure (Months)" value={tenure} onChange={setTenure} unit="Months" min={1} max={36} step={1} />
                                    <CalculatorInput label="Interest Rate" value={rate} onChange={setRate} unit="% p.a." min={4} max={24} step={0.1} />
                                </Grid>

                                <Grid item xs={12} md={5} sx={{ textAlign: 'center', alignSelf: 'center' }}>
                                    <Typography color="text.secondary">Monthly Interest Payment</Typography>
                                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 800 }}>₹{emiForLoan.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>

                                    <Typography color="text.secondary" sx={{ mt: 1 }}>Total Interest Payable (for {tenure} months)</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>₹{totalInterest.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>

                                    <Button variant="contained" onClick={onApplyClick} sx={{
                                        mt: 2, px: 4, py: 1.2, borderRadius: 28, background: 'linear-gradient(90deg,#1fd3a7,#05b2a0)', fontWeight: 800,
                                        transition: 'transform 180ms ease, box-shadow 180ms ease',
                                        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 14px 40px rgba(3,80,72,0.14)' }
                                    }}>Apply Now</Button>
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Section>
    );
};

const BenefitsSection = ({ data }) => (
    <Section>
        <SectionTitle title="Benefits of Loan Against Mutual Funds" align="left" />
        <Grid container spacing={4}>
            {data.map(benefit => (
                <Grid item xs={12} sm={6} md={3} key={benefit.title}>
                    <Box sx={{
                        textAlign: 'center',
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(180deg, rgba(255,255,255,1), rgba(250,255,253,1))',
                        boxShadow: '0 10px 30px rgba(8,29,28,0.04)',
                        height: '100%'
                    }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 72, height: 72, mx: 'auto', mb: 2 }}>
                            {React.cloneElement(benefit.icon, { sx: { color: 'white', fontSize: 30 } })}
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{benefit.title}</Typography>
                        <Typography color="text.secondary">{benefit.description}</Typography>
                    </Box>
                </Grid>
            ))}
        </Grid>
    </Section>
);

const AlternatingBenefitsSection = ({ data }) => (
    <Section sx={{ background: 'linear-gradient(180deg, rgba(250,255,250,1), rgba(255,255,255,1))' }}>
        {data.map((benefit, index) => (
            <Grid container spacing={4} alignItems="center" key={benefit.title} sx={{ mb: 4 }} direction={index % 2 === 0 ? 'row' : 'row-reverse'}>
                <Grid item xs={12} md={6}>
                    <Box sx={{
                        width: '100%', height: { xs: 220, md: 320 }, borderRadius: 3, overflow: 'hidden',
                        boxShadow: '0 20px 50px rgba(10,60,55,0.06)'
                    }}>
                        <img src={benefit.imageUrl} alt={benefit.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 800 }}>{benefit.title}</Typography>
                    <Typography variant="body1" color="text.secondary">{benefit.description}</Typography>
                </Grid>
            </Grid>
        ))}
    </Section>
);

const FAQ = ({ questions }) => (
    <Section>
        <SectionTitle title="Frequently asked questions" align="left" />
        {questions.map((item, index) => (
            <Accordion key={index} sx={{ mb: 1, borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography color="text.secondary">{item.a}</Typography>
                </AccordionDetails>
            </Accordion>
        ))}
    </Section>
);

// ---------- MODAL COMPONENT ----------
const LenderSelectionDialog = ({ open, onClose, onSelectBajaj, onSelect50Fin }) => {
    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: { 
                    borderRadius: 4, 
                    p: 2,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f4fbf9 100%)'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Typography variant="h5" fontWeight={800} color="primary.dark">
                    Select Lender
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Choose a lending partner to proceed with your application.
                </Typography>
                
                <Grid container spacing={2}>
                    {/* OPTION 1: BAJAJ (SDK) */}
                    <Grid item xs={12}>
                        <CardActionArea 
                            onClick={onSelectBajaj}
                            sx={{ 
                                borderRadius: 3, 
                                border: '1px solid rgba(0,0,0,0.08)',
                                overflow: 'hidden',
                                transition: 'all 0.2s',
                                '&:hover': { 
                                    boxShadow: '0 8px 24px rgba(0,100,100,0.1)',
                                    borderColor: 'primary.main',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', background: '#fff' }}>
                                <Avatar sx={{ bgcolor: '#0072bc', width: 56, height: 56, mr: 2 }}>
                                    <TouchAppIcon />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" fontWeight={700}>Small Case</Typography>
                                    <Typography variant="body2" color="text.secondary">Seamless in-app application process.</Typography>
                                </Box>
                                <ArrowForwardIcon color="action" />
                            </Box>
                        </CardActionArea>
                    </Grid>

                    {/* OPTION 2: 50FIN (LINK) */}
                    <Grid item xs={12}>
                        <CardActionArea 
                            onClick={onSelect50Fin}
                            sx={{ 
                                borderRadius: 3, 
                                border: '1px solid rgba(0,0,0,0.08)',
                                overflow: 'hidden',
                                transition: 'all 0.2s',
                                '&:hover': { 
                                    boxShadow: '0 8px 24px rgba(0,100,100,0.1)',
                                    borderColor: 'primary.main',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', background: '#fff' }}>
                                <Avatar sx={{ bgcolor: '#00cba0', width: 56, height: 56, mr: 2 }}>
                                    <LaunchIcon />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" fontWeight={700}>50Fin</Typography>
                                    <Typography variant="body2" color="text.secondary">Apply directly on partner website.</Typography>
                                </Box>
                                <ArrowForwardIcon color="action" />
                            </Box>
                        </CardActionArea>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

// ---------- MAIN PAGE ----------
const LamfPage = () => {
    const mode = useSelector((state) => state.theme.mode);
    const theme = getAppTheme(mode);
    const seoData = getSeoData("/loan-against-mutual-funds");
    
    // Modal State
    const [lenderModalOpen, setLenderModalOpen] = useState(false);

    const handleApplyClick = () => {
        setLenderModalOpen(true);
    };

    // --- Provider 1 Logic: Bajaj (SDK) ---
    const handleBajajApply = async () => {
        setLenderModalOpen(false); // Close modal
        try {
            const payload = { intent: "LOAN_APPLICATION", lender: "bajaj_finserv" };
            const response = await lamfApi.create(payload);
            let interaction_token = response?.data?.interactionToken;
            
            if (!lasSdkInstance) { alert("ScLoan SDK not loaded. Reload page or contact support."); return; }
            if (!interaction_token) { alert("Could not obtain interaction token. Try again."); return; }
            
            try {
                await lasSdkInstance.apply({ interaction_token });
                alert("Application completed successfully.");
            } catch (err) {
                if (err && err.name === "ScLoanError") {
                    if (err.code === 1012) console.log("User cancelled");
                    else if (err.code === 4003) alert("Session expired. Please try again.");
                    else alert(err.message || "An error occurred during application.");
                } else alert("An unexpected error occurred. Check console for details.");
            }
        } catch (error) {
            console.error("LAMF create error:", error);
            alert("Failed to start LAMF request. Try again.");
        }
    };

    // --- Provider 2 Logic: 50Fin (Link) ---
    const handle50FinApply = () => {
        setLenderModalOpen(false); // Close modal
        window.open(PROVIDER_2_LINK, '_blank', 'noopener,noreferrer');
    };

    return (
        <ThemeProvider theme={theme}>
            <SEO {...seoData} />
            <Box sx={{ background: 'linear-gradient(180deg,#f8fffb,#ffffff)' }}>
                <HeroSection data={heroData} onApplyClick={handleApplyClick} />
                <RateBand />
                <StepsSection />
                <LAMFCalculatorSection onApplyClick={handleApplyClick} />
                <BenefitsSection data={benefitsData} />
                <AlternatingBenefitsSection data={alternatingBenefitsData} />
                <FAQ questions={faqData} />

                <Box component="footer" sx={{ py: 6, background: '#fafafa', mt: 6 }}>
                    <Container maxWidth="lg">
                        <Typography variant="caption" color="text.secondary" component="p" textAlign="center">
                            Disclaimer: Loan Against Mutual Funds (LAMF) terms including interest rates, LTV and processing timelines vary by lender and fund type. Please verify exact details and eligibility with the lender before applying.
                        </Typography>
                    </Container>
                </Box>
            </Box>

            {/* Lender Selection Modal */}
            <LenderSelectionDialog 
                open={lenderModalOpen}
                onClose={() => setLenderModalOpen(false)}
                onSelectBajaj={handleBajajApply}
                onSelect50Fin={handle50FinApply}
            />
        </ThemeProvider>
    );
};

export default LamfPage;